import { isEmpty } from 'lodash'
import { TransitionAsset } from 'models/Asset'
import * as ActionTypes from '../ActionTypes'
import { SOURCE_FILE_TYPES } from '~/enums'
import * as Selectors from '~/selectors'
import { isCrossingTransition } from '~/helpers/assets/isCrossingTransition'

const shiftNeighborAssets = (changedProperties, state, rightAssetId, nextValue) => {
  const neighborCrossingTransition = state.layer.assets.find(a => a instanceof TransitionAsset
    && a.leftVideoAssetId === rightAssetId && a.isMountBetween)

  if (neighborCrossingTransition) {
    if (changedProperties.has(neighborCrossingTransition.id)) {
      // eslint-disable-next-line no-param-reassign
      changedProperties
        .get(neighborCrossingTransition.id)
        .next
        .startTime -= nextValue
    } else {
      changedProperties.set(neighborCrossingTransition.id, {
        next: {
          startTime: neighborCrossingTransition.startTime - nextValue,
          duration: neighborCrossingTransition.duration,
        },
        prev: {
          startTime: neighborCrossingTransition.startTime,
          duration: neighborCrossingTransition.duration,
        },
      })
    }
    const RAsset = state.layer.assets
      .find(a => neighborCrossingTransition.rightVideoAssetId === a.id)
    if (RAsset) {
      if (changedProperties.has(RAsset.id)) {
        // eslint-disable-next-line no-param-reassign
        changedProperties.get(RAsset.id).next.startTime -= nextValue
      } else {
        changedProperties.set(RAsset.id, {
          next: {
            startTime: RAsset.startTime - nextValue,
            duration: RAsset.duration,
            mediaStart: RAsset.mediaStart,
          },
          prev: {
            startTime: RAsset.startTime,
            duration: RAsset.duration,
            mediaStart: RAsset.mediaStart,
          },
        })
      }
      shiftNeighborAssets(changedProperties, state, RAsset.id, nextValue)
    }
  }
}

const isBoundsIntersection = ({
  RAssetNextStartTime,
  existingTransLDuration,
  existingTransRDuration,
  LAssetNextStartTime,
  LAssetNextEndTime,
  RAssetNextEndTime,
}) => (RAssetNextStartTime < (LAssetNextStartTime + existingTransLDuration)
  || LAssetNextEndTime > RAssetNextEndTime - existingTransRDuration)

export const getPermissions = (state, transitionParams) => {
  const changedProperties = new Map()
  let maxValue = 0
  if (!isEmpty(transitionParams)) {
    for (const [ trId, value ] of transitionParams) {
      const {
        rightAssetId,
        leftAssetId,
        diff,
        transitionAsset,
        prevDuration,
      } = value
      const rightAsset = state.layer.assets.find(a => a.id === rightAssetId)
      const leftAsset = state.layer.assets.find(a => a.id === leftAssetId)

      const existingTransR = state.layer
        .assets.find(a => (a.isMountOut || isCrossingTransition(a.type))
        && a.leftVideoAssetId === rightAssetId) ?? {}

      const existingTransL = state.layer
        .assets.find(a => (a.isMountIn || isCrossingTransition(a.type))
        && a.rightVideoAssetId === leftAssetId) ?? {}

      if (trId && !changedProperties.has(trId)) {
        const { startTime, duration } = transitionAsset
        changedProperties.set(trId, {
          next: { startTime, duration },
          prev: { startTime, duration },
        })
      }
      if (rightAssetId && !changedProperties.has(rightAssetId)) {
        const { startTime, duration, mediaStart } = rightAsset
        changedProperties.set(rightAssetId, {
          next: { startTime, duration, mediaStart },
          prev: { startTime, duration, mediaStart },
        })
      }
      if (leftAssetId && !changedProperties.has(leftAssetId)) {
        const { startTime, duration, mediaStart } = leftAsset
        changedProperties.set(leftAssetId, {
          next: { startTime, duration, mediaStart },
          prev: { startTime, duration, mediaStart },
        })
      }

      if (existingTransR && !changedProperties.has(existingTransR.id)) {
        const { startTime, duration } = existingTransR
        changedProperties.set(existingTransR.id, {
          next: { startTime, duration },
          prev: { startTime, duration },
        })
      }

      if (existingTransL && !changedProperties.has(existingTransL.id)) {
        const { startTime, duration } = existingTransL
        changedProperties.set(existingTransL.id, {
          next: { startTime, duration },
          prev: { startTime, duration },
        })
      }

      const existingTransRDuration = changedProperties.get(existingTransR.id)?.next.duration ?? 0
      const existingTransLDuration = changedProperties.get(existingTransL.id)?.next.duration ?? 0

      if (rightAsset && leftAsset && transitionAsset.isMountBetween) { // DISSOLVE type
        if (diff >= 0) {
          let durationChange = diff
          const { mediaPlayerDuration: mediaPlayerDurationL } = leftAsset

          const durationL = changedProperties.get(leftAssetId).next.duration
          const mediaStartL = changedProperties.get(leftAssetId).next.mediaStart
          const startTimeL = changedProperties.get(leftAssetId).next.startTime
          const endTimeL = changedProperties.get(leftAssetId).next.startTime
              + changedProperties.get(leftAssetId).next.duration

          const startTimeR = changedProperties.get(rightAssetId).next.startTime
          const mediaStartR = changedProperties.get(rightAssetId).next.mediaStart
          const endTimeR = changedProperties.get(rightAssetId).next.startTime
              + changedProperties.get(rightAssetId).next.duration

          const rightExpandDuration = mediaStartR
          const leftExpandDuration = mediaPlayerDurationL - durationL - mediaStartL

          let maxRAssetExpand = Math.max(0, Math.min(rightExpandDuration,
            startTimeR - (startTimeL + existingTransLDuration)))
          let maxLAssetExpand = Math.max(0, Math.min(leftExpandDuration,
            (endTimeR - existingTransRDuration) - endTimeL))
          let maxShift = Math.max(0, Math.min(startTimeR - (startTimeL + existingTransLDuration),
            (endTimeR - existingTransRDuration) - endTimeL))
          const maxEqualIncrement = Math.max(0, Math.min(maxRAssetExpand,
            maxLAssetExpand, durationChange / 2))

          let nextValue = maxEqualIncrement
          maxValue = changedProperties.get(trId).next.duration
          /** start ** Equal expand. Left to left, right to right */
          if (isBoundsIntersection({
            LAssetNextStartTime: changedProperties.get(leftAssetId).next.startTime,
            LAssetNextEndTime: changedProperties.get(leftAssetId).next.duration
              + nextValue + changedProperties.get(leftAssetId).next.startTime,
            existingTransLDuration,
            existingTransRDuration,
            RAssetNextStartTime: changedProperties.get(rightAssetId).next.startTime - nextValue,
            RAssetNextEndTime: (changedProperties.get(rightAssetId).next.startTime - nextValue)
              + changedProperties.get(rightAssetId).next.duration + nextValue,
          })) {
            continue
          }
          changedProperties.get(leftAssetId).next.duration += nextValue
          changedProperties.get(rightAssetId).next.startTime -= nextValue
          changedProperties.get(rightAssetId).next.mediaStart -= nextValue
          changedProperties.get(rightAssetId).next.duration += nextValue
          changedProperties.get(trId).next.startTime -= nextValue
          changedProperties.get(trId).next.duration += nextValue * 2

          maxRAssetExpand -= nextValue
          maxLAssetExpand -= nextValue
          durationChange -= nextValue * 2
          maxShift -= nextValue
          maxValue = changedProperties.get(trId).next.duration
          /** end ** Equal expand. Left to left, right to right  */


          /** start ** Expand right asset if right expansion duration exists */
          nextValue = Math.min(maxRAssetExpand, durationChange)
          if (isBoundsIntersection({
            LAssetNextStartTime: changedProperties.get(leftAssetId).next.startTime,
            LAssetNextEndTime: changedProperties.get(leftAssetId).next.duration
              + changedProperties.get(leftAssetId).next.startTime,
            existingTransLDuration,
            existingTransRDuration,
            RAssetNextStartTime: changedProperties.get(rightAssetId).next.startTime - nextValue,
            RAssetNextEndTime: (changedProperties.get(rightAssetId).next.startTime - nextValue)
              + changedProperties.get(rightAssetId).next.duration + nextValue,
          })) {
            continue
          }
          changedProperties.get(rightAssetId).next.startTime -= nextValue
          changedProperties.get(rightAssetId).next.mediaStart -= nextValue
          changedProperties.get(rightAssetId).next.duration += nextValue
          changedProperties.get(trId).next.startTime -= nextValue
          changedProperties.get(trId).next.duration += nextValue

          durationChange -= nextValue
          maxRAssetExpand -= nextValue
          maxShift -= nextValue
          maxValue = changedProperties.get(trId).next.duration
          /** end ** Expand right asset if right expansion duration exists */

          /** start ** Expand left asset if left expansion duration exists */
          nextValue = Math.min(maxLAssetExpand, durationChange)
          if (isBoundsIntersection({
            LAssetNextStartTime: changedProperties.get(leftAssetId).next.startTime,
            LAssetNextEndTime: changedProperties.get(leftAssetId).next.duration
              + nextValue + changedProperties.get(leftAssetId).next.startTime,
            existingTransLDuration,
            existingTransRDuration,
            RAssetNextStartTime: changedProperties.get(rightAssetId).next.startTime,
            RAssetNextEndTime: changedProperties.get(rightAssetId).next.startTime
              + changedProperties.get(rightAssetId).next.duration,
          })) {
            continue
          }
          changedProperties.get(trId).next.duration += nextValue
          changedProperties.get(leftAssetId).next.duration += nextValue

          maxLAssetExpand -= nextValue
          durationChange -= nextValue
          maxValue = changedProperties.get(trId).next.duration
          /** end ** Expand left asset if right expansion duration exists */


          /** start Shift right asset */
          nextValue = Math.max(0, Math.min(maxShift, durationChange))
          changedProperties.get(rightAssetId).next.startTime -= nextValue
          changedProperties.get(trId).next.startTime -= nextValue
          changedProperties.get(trId).next.duration += nextValue

          shiftNeighborAssets(changedProperties, state, rightAssetId, nextValue)

          durationChange -= nextValue
          maxShift -= nextValue
          maxValue = changedProperties.get(trId).next.duration
        } else {
          changedProperties.get(trId).next.startTime += (diff / 2) * -1
          changedProperties.get(trId).next.duration -= diff * -1
          changedProperties.get(rightAssetId).next.startTime += (diff / 2) * -1
          changedProperties.get(rightAssetId).next.mediaStart += (diff / 2) * -1
          changedProperties.get(rightAssetId).next.duration -= (diff / 2) * -1
          changedProperties.get(leftAssetId).next.duration -= (diff / 2) * -1
        }
        const notSelectedFadeOut = !isEmpty(existingTransR) && !existingTransR?.selected
        if (notSelectedFadeOut) {
          changedProperties.get(existingTransR.id).next
            .startTime = changedProperties.get(rightAssetId).next.startTime
            + changedProperties.get(rightAssetId).next.duration - existingTransRDuration
        }
        /** end Shift right asset */
      } else if (rightAsset && transitionAsset.isMountIn) { // FADE_IN
        maxValue = rightAsset.duration - existingTransRDuration
        const maxDuration = Math.min(maxValue, prevDuration + diff)

        changedProperties.get(trId).next.duration = maxDuration
      } else if (leftAsset && transitionAsset.isMountOut) { // FADE_OUT
        const leftAssetNextStartTime = changedProperties.get(leftAsset.id)?.next.startTime
        const leftAssetNextDuration = changedProperties.get(leftAsset.id)?.next.duration
        maxValue = leftAssetNextDuration - existingTransLDuration

        const maxDuration = Math.min(leftAssetNextDuration - existingTransLDuration,
          prevDuration + diff)
        const startTime = leftAssetNextStartTime + leftAssetNextDuration - maxDuration
        if (diff > 0) {
          changedProperties
            .get(trId)
            .next
            .startTime = Math.min(changedProperties.get(trId).next.startTime, startTime)
          changedProperties
            .get(trId)
            .next
            .duration = Math.max(changedProperties.get(trId).next.duration, maxDuration)
        } else {
          changedProperties
            .get(trId)
            .next
            .startTime = Math.max(changedProperties.get(trId).next.startTime, startTime)
          changedProperties
            .get(trId)
            .next
            .duration = maxDuration
        }
      }
    }
  }

  return { changedProperties: Object.fromEntries(changedProperties), maxValue }
}

export const patchTransition = async (asset, patch, options, dispatch, getState) => {
  const transitionParams = new Map()
  const assets = Selectors.getAssets(getState())

  if (patch.duration) {
    const newTransitionDuration = patch.duration
    assets.forEach(tr => {
      if (tr instanceof TransitionAsset && tr.selected) {
        transitionParams.set(tr.id, {
          leftAssetId: tr.leftVideoAssetId,
          rightAssetId: tr.rightVideoAssetId,
          prevDuration: tr.duration,
          diff: newTransitionDuration - tr.duration,
          transitionType: tr.type,
          transitionId: tr.id,
          transitionAsset: tr,
        })
      }
    })

    const {
      changedProperties,
    } = getPermissions(getState(), transitionParams)

    dispatch(setTransitionProperty(changedProperties))
  } else if (patch.type) {
    const sourceTransitions = Selectors.getSourceFiles(getState(), SOURCE_FILE_TYPES.TRANSITIONS)
    const { url, name, fileId } = sourceTransitions.find(tr => tr.type === patch.type)
    assets.forEach(tr => {
      if (tr instanceof TransitionAsset && tr.selected) {
        transitionParams.set(tr.id, {
          next: {
            type: patch.type,
            name,
            id: tr.id,
            url,
            fileId,
          },
          prev: {
            type: tr.type,
            name: tr.name,
            id: tr.id,
            url: tr.url,
            fileId: tr.fileId,
          },
        })
      }
    })
    dispatch(setTransitionProperty(Object.fromEntries(transitionParams)))
  }
}

export function setTransitionProperty(changedProperties, isRollback = false) {
  return ({
    type: ActionTypes.SET_TRANSITION_PROPERTY,
    payload: {
      changedProperties,
      isRollback,
    },
  })
}
